/**
 * Geography Student Notes - Years 11-13
 * Based on Vanuatu National Syllabus
 * Comprehensive study materials for all strands
 */

// Year 11 Geography Student Notes
export const year11GeographyStudentNotes = {
  strand1: {
    title: "Natural Processes",
    overview: "Understanding Earth's physical systems, geological processes, and natural resources",
    image: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Earth in Space, Rock Structures, and Landforms",
        content: `
## The Earth in Space

### Earth's Position in the Solar System
- **Distance from Sun**: Approximately 150 million km (1 Astronomical Unit)
- **Orbital period**: 365.25 days
- **Rotation period**: 24 hours
- **Tilt**: 23.5° - causes seasons

### Earth's Layers
| Layer | Thickness | Composition |
|-------|-----------|-------------|
| Crust | 5-70 km | Silicate rocks |
| Mantle | 2,900 km | Semi-molten rock |
| Outer Core | 2,200 km | Liquid iron/nickel |
| Inner Core | 1,250 km | Solid iron/nickel |

### Rock Types

**1. Igneous Rocks**
- Formed from cooled magma or lava
- Examples: Basalt, Granite, Obsidian
- Vanuatu example: Volcanic rocks on Tanna

**2. Sedimentary Rocks**
- Formed from compressed sediments
- Examples: Limestone, Sandstone, Shale
- Vanuatu example: Raised coral limestone

**3. Metamorphic Rocks**
- Formed by heat and pressure transformation
- Examples: Marble, Slate, Quartzite

### Key Landforms
- **Mountains**: Formed by tectonic forces
- **Valleys**: Created by erosion
- **Plains**: Flat areas from deposition
- **Plateaus**: Elevated flat areas
`,
        keyTerms: ["lithosphere", "asthenosphere", "rock cycle", "landform", "topography"],
        practiceQuestions: [
          { q: "Name the three main types of rocks and give one example of each.", a: "Igneous (basalt), Sedimentary (limestone), Metamorphic (marble)" },
          { q: "What causes Earth's seasons?", a: "The 23.5° tilt of Earth's axis as it orbits the Sun" },
          { q: "Which layer of Earth is liquid?", a: "The outer core, made of liquid iron and nickel" }
        ],
        examTips: "Always draw clear diagrams of Earth's layers with labels. Use Vanuatu examples when possible."
      },
      {
        id: "unit1.2",
        title: "Plate Tectonics, Earthquakes and Volcanoes",
        content: `
## Plate Tectonics Theory

### The Theory
- Earth's crust is divided into large plates
- Plates float on the semi-molten mantle
- Plate movement causes earthquakes and volcanoes

### Types of Plate Boundaries

**1. Divergent (Constructive)**
- Plates move apart
- New crust forms
- Example: Mid-Atlantic Ridge

**2. Convergent (Destructive)**
- Plates collide
- One plate subducts under another
- Example: Pacific Plate under Indo-Australian Plate (Vanuatu!)

**3. Transform (Conservative)**
- Plates slide past each other
- No crust created or destroyed
- Example: San Andreas Fault

### Earthquakes

**Causes:**
- Sudden release of energy along fault lines
- Plate boundary movements
- Volcanic activity

**Measurement:**
- Richter Scale (magnitude)
- Modified Mercalli Scale (intensity)

**Vanuatu Context:**
- Located on the Pacific Ring of Fire
- Frequent seismic activity
- Subduction zone creates deep earthquakes

### Volcanoes

**Types:**
| Type | Shape | Eruption Style | Example |
|------|-------|----------------|---------|
| Shield | Broad, gentle | Effusive | Mauna Loa |
| Composite | Tall, steep | Explosive | Mt. Yasur |
| Cinder Cone | Small, steep | Short explosive | Lopevi |

**Vanuatu Volcanoes:**
- **Mt. Yasur** (Tanna) - World's most accessible active volcano
- **Ambae** - Large volcanic island, 2017-2018 eruptions
- **Ambrym** - Lava lake, significant 2018 eruption
`,
        keyTerms: ["subduction", "magnitude", "epicenter", "focus", "pyroclastic flow", "Ring of Fire"],
        practiceQuestions: [
          { q: "What type of plate boundary exists near Vanuatu?", a: "Convergent boundary - the Pacific Plate subducts under the Indo-Australian Plate" },
          { q: "Name two active volcanoes in Vanuatu.", a: "Mt. Yasur and Ambae (or Ambrym)" },
          { q: "What scale measures earthquake intensity?", a: "Modified Mercalli Scale measures intensity; Richter Scale measures magnitude" }
        ],
        examTips: "Draw plate boundary diagrams clearly. Label all parts including direction of movement."
      },
      {
        id: "unit1.3",
        title: "Weathering, Fluvial and Coastal Processes",
        content: `
## Weathering

### Types of Weathering

**Physical (Mechanical) Weathering:**
- Freeze-thaw action
- Thermal expansion
- Root growth
- Salt crystallization

**Chemical Weathering:**
- Oxidation (rusting)
- Carbonation (limestone dissolution)
- Hydrolysis
- Acid rain effects

**Biological Weathering:**
- Root action
- Burrowing animals
- Organic acids from decomposition

## Fluvial Processes (Rivers)

### River Processes

**Erosion Types:**
- **Hydraulic action**: Force of water
- **Abrasion**: Rocks grinding riverbed
- **Attrition**: Rocks hitting each other
- **Corrosion**: Chemical dissolution

**Transportation Methods:**
- Traction (rolling large stones)
- Saltation (bouncing)
- Suspension (carried in water)
- Solution (dissolved minerals)

### River Landforms
- **Upper course**: V-shaped valleys, waterfalls, rapids
- **Middle course**: Meanders, floodplains
- **Lower course**: Deltas, oxbow lakes

## Coastal Processes

### Coastal Erosion
- Wave action (hydraulic and abrasion)
- Creates cliffs, caves, arches, stacks

### Coastal Deposition
- Creates beaches, spits, bars, tombolos

### Vanuatu Coastal Features
- Coral reefs and lagoons
- Raised coral terraces
- Black sand volcanic beaches
- Mangrove coastlines
`,
        keyTerms: ["erosion", "deposition", "transportation", "weathering", "meander", "delta"],
        practiceQuestions: [
          { q: "What is the difference between physical and chemical weathering?", a: "Physical weathering breaks rocks without changing their chemical composition; chemical weathering changes the rock's chemical structure" },
          { q: "Name the four types of river erosion.", a: "Hydraulic action, abrasion, attrition, and corrosion" },
          { q: "What landforms are created by coastal deposition?", a: "Beaches, spits, bars, and tombolos" }
        ],
        examTips: "Use diagrams to show river and coastal processes. Include arrows to show direction of movement."
      },
      {
        id: "unit1.4",
        title: "Atmospheric Processes and Effects",
        content: `
## Weather and Climate

### Weather vs Climate
- **Weather**: Day-to-day atmospheric conditions
- **Climate**: Average weather over 30+ years

### Factors Affecting Climate

**1. Latitude**
- Closer to equator = warmer
- Vanuatu: 13°-21° South (tropical)

**2. Altitude**
- Higher = cooler
- Temperature drops ~6.5°C per 1000m

**3. Distance from Sea**
- Coastal areas have moderate temperatures
- Maritime influence on Vanuatu islands

**4. Ocean Currents**
- Warm currents raise temperatures
- Cold currents lower temperatures

### Atmospheric Circulation

**Global Wind Patterns:**
- Trade Winds (Tropical)
- Westerlies (Mid-latitudes)
- Polar Easterlies

**Pressure Systems:**
- High pressure = descending air, fair weather
- Low pressure = rising air, clouds, rain

### Tropical Cyclones

**Formation Requirements:**
- Warm ocean water (>26°C)
- Coriolis effect
- Low wind shear
- Atmospheric instability

**Vanuatu Cyclone Season:**
- November to April
- Average 2-3 cyclones per year
- Recent example: Cyclone Pam (2015)

### Climate and Vegetation
- Tropical rainforest (high rainfall)
- Savanna (seasonal rainfall)
- Vanuatu: Tropical rainforest climate
`,
        keyTerms: ["troposphere", "pressure system", "cyclone", "precipitation", "humidity", "trade winds"],
        practiceQuestions: [
          { q: "What is the difference between weather and climate?", a: "Weather is day-to-day conditions; climate is the average weather over 30+ years" },
          { q: "What conditions are needed for tropical cyclone formation?", a: "Warm ocean water (>26°C), Coriolis effect, low wind shear, and atmospheric instability" },
          { q: "Why is Vanuatu's climate tropical?", a: "Due to its location between 13°-21° South latitude, near the equator, surrounded by warm ocean waters" }
        ],
        examTips: "Learn cyclone formation steps in order. Be able to explain how latitude affects climate."
      },
      {
        id: "unit1.5",
        title: "Natural Resources: Energy and Water",
        content: `
## Natural Resources

### Types of Resources

**Renewable Resources:**
- Solar energy
- Wind energy
- Hydroelectric power
- Geothermal energy
- Biomass

**Non-Renewable Resources:**
- Fossil fuels (coal, oil, gas)
- Nuclear fuels
- Minerals

### Energy Resources

**Fossil Fuels:**
- Formed from ancient organic matter
- Major global energy source
- Environmental concerns (climate change)

**Renewable Energy in Vanuatu:**
- **Solar**: High potential due to tropical location
- **Geothermal**: Volcanic activity provides opportunity
- **Hydro**: River systems on larger islands
- **Coconut oil**: Biofuel production

### Water Resources

**Water Cycle:**
1. Evaporation from oceans/lakes
2. Condensation forms clouds
3. Precipitation (rain/snow)
4. Collection in rivers/groundwater
5. Return to ocean

**Water in Vanuatu:**
- Groundwater sources
- River systems
- Rainwater collection
- Freshwater lens on coral islands

**Water Challenges:**
- Seasonal availability
- Saltwater intrusion on small islands
- Climate change impacts
- Contamination risks

### Resource Management
- Sustainable use principles
- Conservation strategies
- Traditional knowledge
- Government policies
`,
        keyTerms: ["renewable", "non-renewable", "sustainable", "conservation", "water cycle", "aquifer"],
        practiceQuestions: [
          { q: "What is the difference between renewable and non-renewable resources?", a: "Renewable resources can be replenished naturally; non-renewable resources are finite and will run out" },
          { q: "Name three renewable energy sources suitable for Vanuatu.", a: "Solar, geothermal, and hydroelectric power" },
          { q: "What is the water cycle?", a: "The continuous movement of water through evaporation, condensation, precipitation, and collection" }
        ],
        examTips: "Focus on Vanuatu examples for natural resources. Understand both benefits and challenges of each resource type."
      }
    ]
  },
  strand2: {
    title: "Cultural Processes",
    overview: "Human geography including population, urbanization, and tourism",
    image: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Population Growth and Movement",
        content: `
## World Population

### Population Growth
- Current world population: ~8 billion
- Growth rate declining but still positive
- Highest growth in developing countries

### Population Measures

**Key Indicators:**
| Indicator | Definition |
|-----------|------------|
| Birth Rate | Births per 1,000 people per year |
| Death Rate | Deaths per 1,000 people per year |
| Natural Increase | Birth rate minus death rate |
| Fertility Rate | Average children per woman |

### Demographic Transition Model

**Stage 1**: High birth and death rates
**Stage 2**: High birth, falling death rates
**Stage 3**: Falling birth rates
**Stage 4**: Low birth and death rates
**Stage 5**: Death rate exceeds birth rate

### Migration

**Types of Migration:**
- **Internal**: Within a country
- **International**: Between countries
- **Rural-urban**: Villages to cities
- **Forced**: Refugees, displacement

### Pacific Migration Patterns

**Push Factors:**
- Limited economic opportunities
- Climate change impacts
- Natural disasters
- Land pressure

**Pull Factors:**
- Employment opportunities
- Education access
- Family connections
- Better services

**Vanuatu Migration:**
- Rural to Port Vila migration
- Seasonal workers to Australia/NZ
- Internal island migration
`,
        keyTerms: ["migration", "birth rate", "death rate", "demographic transition", "push factors", "pull factors"],
        practiceQuestions: [
          { q: "What are push and pull factors?", a: "Push factors drive people away from an area; pull factors attract people to a new area" },
          { q: "Name three push factors for Pacific migration.", a: "Limited economic opportunities, climate change impacts, and natural disasters" },
          { q: "What is the demographic transition model?", a: "A model showing how birth and death rates change as a country develops through stages" }
        ],
        examTips: "Learn to interpret population pyramids and calculate population statistics."
      },
      {
        id: "unit2.2",
        title: "Urbanization and Urban Settlements",
        content: `
## Urbanization

### Definition
The increasing proportion of people living in urban areas

### Global Trends
- Over 50% of world population now urban
- Fastest urbanization in developing countries
- Megacities (>10 million) increasing

### Urban Settlement Patterns

**Urban Land Use Models:**
1. **Concentric Zone Model** (Burgess)
   - CBD at center
   - Zones radiate outward

2. **Sector Model** (Hoyt)
   - Sectors based on transport routes
   - Similar uses along routes

3. **Multiple Nuclei Model**
   - Multiple centers of activity
   - Reflects modern cities

### Urban Problems

**Housing:**
- Informal settlements (squatter areas)
- Overcrowding
- High costs

**Infrastructure:**
- Water supply
- Sanitation
- Electricity
- Roads

**Social Issues:**
- Unemployment
- Crime
- Health issues
- Inequality

### Port Vila - Vanuatu's Capital

**Growth:**
- Population ~50,000
- Rapid growth from rural-urban migration

**Challenges:**
- Informal settlements
- Water supply
- Waste management
- Traffic congestion

**Solutions:**
- Urban planning
- Infrastructure investment
- Community development
`,
        keyTerms: ["urbanization", "CBD", "informal settlement", "urban sprawl", "infrastructure"],
        practiceQuestions: [
          { q: "What is urbanization?", a: "The increasing proportion of a population living in urban areas" },
          { q: "Name three urban problems found in Port Vila.", a: "Informal settlements, water supply issues, and waste management challenges" },
          { q: "Describe the Concentric Zone Model.", a: "An urban model with the CBD at the center and zones radiating outward in rings" }
        ],
        examTips: "Be able to draw urban models. Use Port Vila as a Pacific example."
      },
      {
        id: "unit2.3",
        title: "Tourism: Destinations, Growth, Trends",
        content: `
## Tourism Industry

### Definition
Travel for recreation, leisure, or business purposes

### Types of Tourism
- **Mass tourism**: Large groups, package holidays
- **Ecotourism**: Nature-based, sustainable
- **Cultural tourism**: Heritage, traditions
- **Adventure tourism**: Activities, experiences

### Global Tourism Growth
- Pre-COVID: 1.5 billion international arrivals
- Major industry worldwide
- Significant employer

### Tourism in the Pacific

**Popular Destinations:**
- Fiji
- Vanuatu
- Samoa
- Tahiti
- Palau

**Attractions:**
- Beaches and coral reefs
- Traditional cultures
- Adventure activities
- Climate

### Tourism in Vanuatu

**Key Attractions:**
- Mt. Yasur volcano
- Blue holes
- Traditional villages
- Diving/snorkeling
- Cultural festivals

**Tourism Benefits:**
- Foreign exchange
- Employment
- Infrastructure development
- Cultural exchange

**Tourism Challenges:**
- Environmental impact
- Cultural changes
- Economic dependency
- Seasonal variations

### Sustainable Tourism
- Minimize environmental impact
- Support local communities
- Preserve culture
- Economic benefits distributed fairly
`,
        keyTerms: ["ecotourism", "sustainable tourism", "mass tourism", "carrying capacity", "leakage"],
        practiceQuestions: [
          { q: "What is ecotourism?", a: "Nature-based tourism that minimizes environmental impact and supports conservation" },
          { q: "Name three tourist attractions in Vanuatu.", a: "Mt. Yasur volcano, blue holes, and traditional villages" },
          { q: "What are the challenges of tourism for Pacific islands?", a: "Environmental impact, cultural changes, economic dependency, and seasonal variations" }
        ],
        examTips: "Focus on both positive and negative impacts of tourism. Use specific Vanuatu examples."
      }
    ]
  },
  strand3: {
    title: "Local, Regional and Global Studies",
    overview: "Focus on Vanuatu's geography, climate, resources, and conservation",
    image: "https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Islands and Geology of Vanuatu",
        content: `
## Vanuatu's Geography

### Location
- Southwest Pacific Ocean
- 13°-21° South latitude
- 166°-171° East longitude
- Part of Melanesia

### The Islands
- **Total islands**: 83 (65 inhabited)
- **Total area**: 12,189 km²
- **Exclusive Economic Zone**: 680,000 km²

### Major Island Groups

**Northern Islands:**
- Torba Province (Torres and Banks)
- Small volcanic islands

**Central Islands:**
- Sanma Province (Santo - largest island)
- Penama Province (Pentecost, Ambae, Maewo)
- Malampa Province (Malekula, Ambrym)

**Southern Islands:**
- Shefa Province (Efate - capital island)
- Tafea Province (Tanna, Aneityum)

### Geological Formation

**Tectonic Setting:**
- Located on Pacific Ring of Fire
- Subduction of Pacific Plate under Indo-Australian Plate
- Creates New Hebrides Trench

**Island Types:**
1. **Volcanic islands**: Most Vanuatu islands
2. **Raised coral islands**: Formed from uplifted reefs
3. **Mixed**: Volcanic base with coral overlay

### Volcanic Activity
- 9 active volcanoes
- Highest: Mt. Tabwemasana (1,879m) on Santo
- Most active: Mt. Yasur on Tanna
`,
        keyTerms: ["archipelago", "subduction", "volcanic arc", "coral reef", "tectonic plate"],
        practiceQuestions: [
          { q: "How many islands make up Vanuatu?", a: "83 islands, of which 65 are inhabited" },
          { q: "What tectonic process formed Vanuatu's islands?", a: "Subduction of the Pacific Plate under the Indo-Australian Plate" },
          { q: "What is Vanuatu's largest island?", a: "Santo (also called Espiritu Santo) in Sanma Province" }
        ],
        examTips: "Know the provinces and major islands. Be able to draw a map of Vanuatu."
      },
      {
        id: "unit3.2",
        title: "Climate of Vanuatu",
        content: `
## Vanuatu's Climate

### Climate Type
- **Classification**: Tropical maritime
- **Characteristics**: Warm, humid year-round

### Seasons

**Wet Season (November-April):**
- Higher rainfall
- Warmer temperatures (25-33°C)
- Tropical cyclone risk
- Higher humidity

**Dry Season (May-October):**
- Lower rainfall
- Cooler temperatures (20-28°C)
- Southeast trade winds
- Lower humidity

### Climate Factors

**1. Latitude**
- Tropical location (13°-21°S)
- High sun angle year-round

**2. Ocean Influence**
- Surrounded by warm Pacific waters
- Moderating effect on temperatures

**3. Trade Winds**
- Southeast trade winds dominant
- Bring moisture to windward coasts

**4. ENSO (El Niño/La Niña)**
- El Niño: Drier conditions
- La Niña: Wetter conditions

### Rainfall Distribution
- Windward (east) coasts: Higher rainfall
- Leeward (west) coasts: Lower rainfall
- Northern islands: Generally wetter
- Average: 2,000-4,000mm per year

### Vegetation Zones
- Tropical rainforest (lowland)
- Cloud forest (highlands)
- Coastal mangroves
- Savanna (drier areas)

### Climate Hazards
- Tropical cyclones
- Droughts (El Niño years)
- Flooding
- Sea level rise
`,
        keyTerms: ["tropical maritime", "trade winds", "ENSO", "El Niño", "La Niña", "monsoon"],
        practiceQuestions: [
          { q: "What type of climate does Vanuatu have?", a: "Tropical maritime climate" },
          { q: "When is Vanuatu's wet season?", a: "November to April" },
          { q: "How does El Niño affect Vanuatu?", a: "El Niño brings drier conditions to Vanuatu" }
        ],
        examTips: "Know seasonal patterns and how ENSO affects Pacific weather."
      },
      {
        id: "unit3.3",
        title: "Cultural and Political Regions of Vanuatu",
        content: `
## Administrative Regions

### Provinces of Vanuatu

| Province | Capital | Major Islands |
|----------|---------|---------------|
| Torba | Sola | Torres, Banks |
| Sanma | Luganville | Santo, Malo |
| Penama | Saratamata | Pentecost, Ambae, Maewo |
| Malampa | Lakatoro | Malekula, Ambrym |
| Shefa | Port Vila | Efate, Shepherds |
| Tafea | Isangel | Tanna, Erromango, Aneityum |

### Government Structure
- **Capital**: Port Vila
- **Government**: Parliamentary republic
- **President**: Head of state
- **Prime Minister**: Head of government
- **Parliament**: 52 members

### Cultural Regions

**Language Groups:**
- Over 100 indigenous languages
- Bislama (national language)
- English and French (official)

**Traditional Governance:**
- Chiefs and elders
- Custom (Kastom) law
- Village councils

### Urban vs Rural

**Urban Centers:**
- Port Vila (~50,000)
- Luganville (~16,000)

**Rural Areas:**
- Village-based living
- Subsistence agriculture
- Traditional customs stronger

### Regional Boundaries
- Based on colonial history
- Island groupings
- Cultural similarities
- Administrative convenience
`,
        keyTerms: ["province", "Bislama", "Kastom", "chief", "parliament"],
        practiceQuestions: [
          { q: "How many provinces does Vanuatu have?", a: "Six provinces" },
          { q: "What are Vanuatu's official languages?", a: "English and French, with Bislama as the national language" },
          { q: "What is Kastom?", a: "Traditional custom and law that governs many aspects of Ni-Vanuatu life" }
        ],
        examTips: "Learn all province names and capitals. Understand the dual colonial heritage."
      },
      {
        id: "unit3.4",
        title: "Economy of Vanuatu: Natural Resources and Land Use",
        content: `
## Vanuatu's Economy

### Economic Overview
- **GDP**: ~$900 million USD
- **Main sectors**: Services, agriculture, tourism
- **Currency**: Vatu (VUV)

### Land Use

**Agriculture (65% of land):**
- Subsistence farming
- Cash crops:
  - Copra (coconut)
  - Cocoa
  - Kava
  - Coffee
  - Beef cattle

**Forest (36% of land):**
- Logging (limited)
- Non-timber forest products
- Conservation areas

### Natural Resources

**Marine Resources:**
- Fish and seafood
- Coral reefs
- Exclusive Economic Zone

**Mineral Resources:**
- Limited development
- Manganese deposits
- Potential for deep-sea mining

**Forestry:**
- Sandalwood
- Timber (controlled)
- Non-timber products

### Land Tenure
- 90% customary land ownership
- Community-based decisions
- Leasehold for development

### Resource Management Challenges
- Overfishing
- Deforestation
- Soil degradation
- Climate change impacts

### Sustainable Development
- Protected areas
- Community-based resource management
- Traditional knowledge integration
- International support
`,
        keyTerms: ["subsistence", "cash crop", "copra", "kava", "customary land", "EEZ"],
        practiceQuestions: [
          { q: "What are Vanuatu's main cash crops?", a: "Copra, cocoa, kava, coffee, and beef cattle" },
          { q: "What percentage of Vanuatu's land is under customary ownership?", a: "Approximately 90%" },
          { q: "What are the challenges facing Vanuatu's natural resources?", a: "Overfishing, deforestation, soil degradation, and climate change impacts" }
        ],
        examTips: "Understand the difference between subsistence and commercial agriculture."
      },
      {
        id: "unit3.5",
        title: "Biodiversity, Ecosystems and Conservation",
        content: `
## Vanuatu's Biodiversity

### Ecosystems

**Terrestrial Ecosystems:**
- Tropical rainforest
- Montane forest
- Coastal vegetation
- Grasslands/savanna

**Marine Ecosystems:**
- Coral reefs
- Seagrass beds
- Mangroves
- Open ocean

### Key Species

**Endemic Species:**
- Various bird species
- Unique plant species
- Marine species

**Threatened Species:**
- Dugong
- Sea turtles
- Some bird species
- Coral reef species

### Conservation Challenges

**Threats:**
- Habitat loss
- Overharvesting
- Invasive species
- Climate change
- Pollution

**Climate Change Impacts:**
- Coral bleaching
- Sea level rise
- Changed rainfall patterns
- Cyclone intensity

### Conservation Efforts

**Protected Areas:**
- Marine protected areas
- Forest reserves
- Community conservation areas

**Traditional Conservation:**
- Tabu (traditional protected areas)
- Seasonal restrictions
- Customary management

**International Support:**
- SPREP (Secretariat of the Pacific Regional Environment Programme)
- NGO partnerships
- Global Environment Facility

### Community-Based Conservation
- Local ownership and management
- Traditional knowledge integration
- Sustainable use approaches
- Economic benefits to communities
`,
        keyTerms: ["biodiversity", "endemic", "conservation", "ecosystem", "tabu", "SPREP"],
        practiceQuestions: [
          { q: "What is biodiversity?", a: "The variety of life in an area, including species diversity, genetic diversity, and ecosystem diversity" },
          { q: "What is 'tabu' in Vanuatu conservation?", a: "Traditional protected areas where harvesting is restricted or prohibited" },
          { q: "Name three threats to Vanuatu's biodiversity.", a: "Habitat loss, overharvesting, and climate change" }
        ],
        examTips: "Know specific examples of conservation efforts and link traditional and modern approaches."
      }
    ]
  },
  strand4: {
    title: "Geographical Skills and Ideas",
    overview: "Map skills, research methods, and geographical analysis",
    image: "https://images.unsplash.com/photo-1524661135-423995f22d0b?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Map Types, Projections and Conventions",
        content: `
## Types of Maps

### General Purpose Maps
- **Political maps**: Show boundaries and capitals
- **Physical maps**: Show terrain and features
- **Road maps**: Show transportation routes
- **Topographic maps**: Show detailed terrain

### Thematic Maps
- **Choropleth**: Shaded areas showing data
- **Dot distribution**: Dots showing locations
- **Isoline**: Lines connecting equal values
- **Flow maps**: Show movement/connections

### Map Projections

**Why Projections?**
- Earth is spherical
- Maps are flat
- Distortion is unavoidable

**Common Projections:**

| Projection | Preserves | Distorts | Use |
|------------|-----------|----------|-----|
| Mercator | Shape | Area (at poles) | Navigation |
| Peters | Area | Shape | Statistics |
| Robinson | Balance | Edges slightly | General use |

### Map Conventions

**Title**: What the map shows
**Legend/Key**: Symbols explained
**Scale**: Distance relationship
**North Arrow**: Orientation
**Source**: Data origin
**Date**: When created

### Scale Types
- **Linear (bar) scale**: Distance shown as line
- **Ratio scale**: 1:50,000
- **Statement scale**: "1cm = 1km"
`,
        keyTerms: ["projection", "scale", "legend", "choropleth", "topographic", "thematic"],
        practiceQuestions: [
          { q: "Why do maps need projections?", a: "Because Earth is spherical and maps are flat, causing unavoidable distortion" },
          { q: "What is a choropleth map?", a: "A thematic map using shaded areas to represent data values" },
          { q: "What are the essential elements every map should have?", a: "Title, legend, scale, north arrow, source, and date" }
        ],
        examTips: "Practice drawing maps with all required conventions. Know when to use different projections."
      },
      {
        id: "unit4.2",
        title: "Grid References, Coordinates and Time Zones",
        content: `
## Grid References

### 4-Figure Grid Reference
- Identifies a grid square
- Format: Easting then Northing
- Example: 2345 (square)

### 6-Figure Grid Reference
- Identifies a point within a square
- More precise location
- Example: 234456

### Rules
1. **Read eastings first** (along the bottom)
2. **Read northings second** (up the side)
3. Remember: "Along the corridor, up the stairs"

## Latitude and Longitude

### Latitude
- Lines running east-west
- Measure north or south of Equator
- 0° at Equator, 90° at poles
- Vanuatu: ~13°S to 21°S

### Longitude
- Lines running north-south
- Measure east or west of Prime Meridian
- 0° at Greenwich, 180° at Date Line
- Vanuatu: ~166°E to 171°E

## Time Zones

### How Time Zones Work
- Earth rotates 360° in 24 hours
- 15° = 1 hour difference
- Based on longitude

### International Date Line
- Located at ~180° longitude
- Cross west: Add a day
- Cross east: Subtract a day

### Vanuatu Time
- UTC+11 hours
- No daylight saving
- East of the Date Line

### Calculating Time Differences
1. Find longitude difference
2. Divide by 15 = hours difference
3. Add for east, subtract for west
`,
        keyTerms: ["grid reference", "latitude", "longitude", "Prime Meridian", "International Date Line", "UTC"],
        practiceQuestions: [
          { q: "What is the correct format for a 6-figure grid reference?", a: "Three numbers for easting (along) followed by three numbers for northing (up)" },
          { q: "What is the latitude range of Vanuatu?", a: "Approximately 13°S to 21°S" },
          { q: "What happens when you cross the International Date Line traveling west?", a: "You add a day (skip forward)" }
        ],
        examTips: "Practice grid references until automatic. Always give eastings before northings."
      },
      {
        id: "unit4.3",
        title: "Practical Skills in Geography",
        content: `
## Types of Graphs

### Bar Graphs
- Compare categories
- Horizontal or vertical bars
- Clearly labeled axes

### Line Graphs
- Show change over time
- Continuous data
- Multiple lines possible

### Pie Charts
- Show proportions
- Total must equal 100%
- Limited categories (5-7)

### Climate Graphs
- Combination graph
- Bars for rainfall
- Line for temperature

### Population Pyramids
- Age-sex structure
- Males left, females right
- Age groups vertical

## Geographical Diagrams

### Cross-sections
- Side view through landscape
- Shows elevation changes
- Used for valleys, mountains

### Sketch Maps
- Simplified map
- Key features only
- Proportional layout

### Annotated Photographs
- Labels on key features
- Explanatory notes
- Geographic analysis

### Flow Diagrams
- Show processes or connections
- Arrows indicate direction
- Sequential or cyclic

## Cartoons and Visual Sources
- Identify the message
- Analyze symbols used
- Consider the perspective
- Link to geographic concepts
`,
        keyTerms: ["graph", "cross-section", "sketch map", "annotation", "climate graph", "population pyramid"],
        practiceQuestions: [
          { q: "What type of graph shows change over time?", a: "Line graph" },
          { q: "What is shown in a population pyramid?", a: "The age-sex structure of a population" },
          { q: "What is a cross-section diagram?", a: "A side view through a landscape showing elevation changes" }
        ],
        examTips: "Practice all graph types. Label axes clearly and include titles."
      },
      {
        id: "unit4.4",
        title: "Research Skills and Essay Writing",
        content: `
## Research Methods

### Types of Data

**Primary Data:**
- Collected firsthand
- Surveys, interviews
- Fieldwork observations
- Measurements

**Secondary Data:**
- Collected by others
- Books, journals
- Government statistics
- Online sources

### Research Steps

1. **Define the question**
   - Clear, focused topic
   - Researchable question

2. **Collect data**
   - Plan methods
   - Gather information

3. **Analyze data**
   - Look for patterns
   - Draw conclusions

4. **Present findings**
   - Organize clearly
   - Support with evidence

### Fieldwork Methods
- Observation and recording
- Surveys and questionnaires
- Interviews
- Measurements and sampling
- Photography and sketching

## Essay Writing

### Essay Structure

**Introduction:**
- Define key terms
- State your argument
- Outline essay structure

**Body Paragraphs:**
- Topic sentence
- Evidence/examples
- Analysis
- Link to question

**Conclusion:**
- Summarize main points
- Restate argument
- No new information

### Geographic Essay Tips
- Use case studies
- Include statistics
- Use geographic terminology
- Draw relevant diagrams
- Reference sources
`,
        keyTerms: ["primary data", "secondary data", "fieldwork", "survey", "analysis", "case study"],
        practiceQuestions: [
          { q: "What is the difference between primary and secondary data?", a: "Primary data is collected firsthand by the researcher; secondary data was collected by others" },
          { q: "What are the main parts of an essay?", a: "Introduction, body paragraphs, and conclusion" },
          { q: "Name three fieldwork methods.", a: "Observation, surveys/questionnaires, and interviews" }
        ],
        examTips: "Always plan essays before writing. Use specific examples and data to support arguments."
      }
    ]
  }
};

// Year 12 Geography Student Notes
export const year12GeographyStudentNotes = {
  strand1: {
    title: "Natural Processes",
    overview: "Advanced study of landforms, natural hazards, and resource management",
    image: "https://images.unsplash.com/photo-1508193638397-1c4234db14d9?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Earth's Landforms and Relief",
        content: `
## Continental and Oceanic Landforms

### Continental Landforms

**Mountain Types:**
- **Fold mountains**: Formed by compression (Himalayas)
- **Block mountains**: Formed by faulting (Sierra Nevada)
- **Volcanic mountains**: Built by eruptions (Mt. Fuji)

**Plains and Plateaus:**
- **Coastal plains**: Low, flat coastal areas
- **Interior plains**: Central flat areas
- **Plateaus**: Elevated flat areas (Deccan Plateau)

### Oceanic Landforms

**Ocean Floor Features:**
- **Continental shelf**: Shallow extension of continent
- **Continental slope**: Steep descent to deep ocean
- **Abyssal plain**: Flat ocean floor
- **Mid-ocean ridges**: Underwater mountain chains
- **Ocean trenches**: Deep subduction zones

**Pacific Ocean Features:**
- Mariana Trench (deepest point)
- Mid-Pacific Mountains
- Numerous seamounts

### Relief Representation

**Contour Lines:**
- Lines joining equal elevation
- Closely spaced = steep slope
- Widely spaced = gentle slope

**Relief Shading:**
- Visual representation of terrain
- Shadow effects show height

### Landform Processes
- Tectonic uplift
- Erosion and weathering
- Deposition
- Volcanic activity
`,
        keyTerms: ["relief", "contour", "continental shelf", "abyssal plain", "mid-ocean ridge", "seamount"],
        practiceQuestions: [
          { q: "What are the three main types of mountains?", a: "Fold mountains, block mountains, and volcanic mountains" },
          { q: "What is the continental shelf?", a: "The shallow underwater extension of a continent before the steep drop to the ocean floor" },
          { q: "What do closely spaced contour lines indicate?", a: "A steep slope" }
        ],
        examTips: "Practice reading contour maps. Know Pacific Ocean features relevant to Vanuatu."
      },
      {
        id: "unit1.2",
        title: "Living with Natural Hazards: Tectonics and Cyclones",
        content: `
## Natural Hazard Management

### Tectonic Hazards in the Pacific

**Earthquake Impacts:**
- Building collapse
- Landslides
- Liquefaction
- Tsunami generation

**Volcanic Impacts:**
- Pyroclastic flows
- Ash fall
- Lahars (mud flows)
- Lava flows

**Case Study: Vanuatu**
- Ambae eruption 2017-2018
- Total island evacuation
- Ash damage to agriculture
- Long-term displacement

### Tropical Cyclones

**Structure:**
- Eye (calm center)
- Eye wall (strongest winds)
- Rain bands (spiral arms)

**Categories (Saffir-Simpson):**
| Category | Wind Speed | Damage |
|----------|------------|--------|
| 1 | 119-153 km/h | Minimal |
| 2 | 154-177 km/h | Moderate |
| 3 | 178-208 km/h | Extensive |
| 4 | 209-251 km/h | Extreme |
| 5 | >251 km/h | Catastrophic |

**Case Study: Cyclone Pam (2015)**
- Category 5 cyclone
- Hit Vanuatu on March 13-14
- 90% of buildings damaged in Port Vila
- 16 confirmed deaths
- Massive agricultural losses

### Disaster Risk Reduction

**Preparedness:**
- Early warning systems
- Evacuation plans
- Emergency supplies
- Public education

**Mitigation:**
- Building codes
- Land use planning
- Infrastructure protection
- Environmental buffers

**Response:**
- Emergency services
- Relief supplies
- Evacuation centers
- Search and rescue

**Recovery:**
- Reconstruction
- Economic support
- Psychological support
- Lessons learned
`,
        keyTerms: ["disaster risk reduction", "preparedness", "mitigation", "response", "recovery", "cyclone"],
        practiceQuestions: [
          { q: "What is the eye of a cyclone?", a: "The calm center of a tropical cyclone with light winds and often clear skies" },
          { q: "What impacts did Cyclone Pam have on Vanuatu?", a: "90% of buildings damaged in Port Vila, 16 deaths, massive agricultural losses" },
          { q: "What are the four phases of disaster management?", a: "Preparedness, mitigation, response, and recovery" }
        ],
        examTips: "Know specific case studies with facts and figures. Understand the disaster management cycle."
      },
      {
        id: "unit1.3",
        title: "Living with Natural Hazards: Tsunamis, Floods and Droughts",
        content: `
## Tsunamis

### How Tsunamis Form
1. Underwater earthquake, volcanic eruption, or landslide
2. Water column displaced
3. Waves radiate outward
4. Waves grow as they approach shore

### Tsunami Characteristics
- Open ocean: Low height, fast (800 km/h)
- Near shore: High height (10-30m), slower
- Multiple waves over hours

### Warning Systems
- Pacific Tsunami Warning Center
- Deep ocean sensors
- Seismic monitoring
- Community sirens

### Pacific Tsunami History
- 2011 Japan tsunami (20,000 deaths)
- 2004 Indian Ocean (230,000 deaths)
- 2009 Samoa tsunami

## Floods

### Types of Floods
- **River floods**: Heavy rain, snowmelt
- **Flash floods**: Sudden, intense rainfall
- **Coastal floods**: Storm surge, high tides
- **Urban floods**: Poor drainage

### Flood Impacts
- Loss of life
- Property damage
- Infrastructure destruction
- Disease spread
- Agricultural losses

### Flood Management
- Dams and levees
- Retention basins
- Channel improvements
- Land use zoning
- Early warning

## Droughts

### Causes
- Below-average rainfall
- El Niño events
- Climate variability
- Human factors (deforestation)

### Impacts
- Water shortages
- Crop failure
- Livestock deaths
- Forest fires
- Economic losses

### Drought Management
- Water storage
- Conservation measures
- Drought-resistant crops
- Emergency response plans
- Climate monitoring
`,
        keyTerms: ["tsunami", "flash flood", "storm surge", "drought", "El Niño", "warning system"],
        practiceQuestions: [
          { q: "How fast can a tsunami travel in the open ocean?", a: "Up to 800 km/h" },
          { q: "What is the difference between a river flood and a flash flood?", a: "River floods develop gradually from sustained rainfall; flash floods occur suddenly from intense rainfall" },
          { q: "How does El Niño affect drought risk in the Pacific?", a: "El Niño brings drier conditions to the western Pacific, increasing drought risk" }
        ],
        examTips: "Know specific examples of each hazard type. Understand both natural and human causes."
      },
      {
        id: "unit1.4",
        title: "Global Distribution and Utilization of Natural Resources",
        content: `
## Global Resource Distribution

### Energy Resources

**Fossil Fuels Distribution:**
- Oil: Middle East, Russia, USA, Venezuela
- Coal: China, USA, India, Australia
- Natural Gas: Russia, Iran, Qatar, USA

**Renewable Energy Potential:**
- Solar: Equatorial and desert regions
- Wind: Coastal and highland areas
- Hydro: Mountainous areas with rivers
- Geothermal: Tectonic active zones

### Mineral Resources
- Iron ore: Australia, Brazil, China
- Copper: Chile, Peru, China
- Gold: China, Australia, Russia
- Bauxite: Australia, China, Guinea

### Water Resources
- Freshwater: Unevenly distributed
- Major rivers: Amazon, Nile, Yangtze
- Groundwater: Aquifers worldwide
- Desalination: Middle East, Australia

## Resource Utilization

### Sustainable Resource Management

**Principles:**
- Maintain resource base
- Equitable distribution
- Intergenerational equity
- Environmental protection

**Strategies:**
- Quotas and regulations
- Protected areas
- Recycling and efficiency
- Alternative resources

### Resource Conflicts
- Competition between nations
- Corporate vs. community interests
- Present vs. future needs
- Economic vs. environmental goals

### Pacific Resource Issues
- Deep-sea mining debates
- Fisheries management
- Forest conservation
- Climate change impacts
`,
        keyTerms: ["sustainable development", "resource management", "fossil fuel", "renewable energy", "aquifer"],
        practiceQuestions: [
          { q: "Where are the world's major oil reserves located?", a: "Middle East, Russia, USA, and Venezuela" },
          { q: "What is sustainable resource management?", a: "Using resources in ways that maintain the resource base for future generations while meeting current needs" },
          { q: "What are the key principles of sustainable development?", a: "Maintain resource base, equitable distribution, intergenerational equity, and environmental protection" }
        ],
        examTips: "Know global distribution patterns. Link resource issues to the Pacific context."
      }
    ]
  },
  strand2: {
    title: "Cultural Processes",
    overview: "Population dynamics, urbanization, and tourism in the Pacific context",
    image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Population Growth: Distribution and Consequences",
        content: `
## Global Population Distribution

### Population Density Patterns
- **High density**: East Asia, South Asia, Europe
- **Medium density**: Americas, Southeast Asia
- **Low density**: Desert, Arctic, mountain regions

### Factors Affecting Distribution
- Climate
- Water availability
- Soil fertility
- Economic opportunity
- Historical factors

## Population Consequences

### Overpopulation Effects
- Resource depletion
- Environmental degradation
- Poverty and inequality
- Food insecurity
- Migration pressure

### Underpopulation Effects
- Labor shortages
- Economic challenges
- Service provision difficulties
- Defense concerns

### Pacific Population Issues
- Rapid growth in some nations
- Migration to urban areas
- Youth bulge (high youth %)
- Aging populations in developed nations

### Population Policies

**Pro-natalist:**
- Encourage births
- Financial incentives
- Family support services
- Example: France

**Anti-natalist:**
- Discourage births
- Family planning
- Education campaigns
- Example: China (historical)

### Pacific Population Management
- Family planning programs
- Education initiatives
- Healthcare improvements
- Economic development
`,
        keyTerms: ["population density", "overpopulation", "underpopulation", "pro-natalist", "anti-natalist"],
        practiceQuestions: [
          { q: "What factors affect population distribution?", a: "Climate, water availability, soil fertility, economic opportunity, and historical factors" },
          { q: "What is a pro-natalist policy?", a: "A government policy that encourages people to have more children" },
          { q: "What are the consequences of overpopulation?", a: "Resource depletion, environmental degradation, poverty, food insecurity, and migration pressure" }
        ],
        examTips: "Be able to analyze population data and policies. Use Pacific examples."
      },
      {
        id: "unit2.2",
        title: "Urbanization: Planning and Management",
        content: `
## Urbanization Process

### Causes of Urbanization
- **Pull factors**: Jobs, services, lifestyle
- **Push factors**: Rural poverty, land shortage, disasters

### Urbanization in the Pacific
- Rapid growth in capital cities
- Port Vila, Suva, Honiara growing fastest
- Informal settlements expanding

### Urban Planning

**Key Principles:**
- Zoning and land use
- Transportation networks
- Service provision
- Environmental protection
- Community involvement

**Planning Tools:**
- Master plans
- Zoning regulations
- Building codes
- Environmental assessments
- Public consultation

### Urban Challenges

**Pacific Urban Issues:**
- Informal settlements (squatter areas)
- Inadequate water and sanitation
- Traffic congestion
- Waste management
- Unemployment

### Solutions and Management

**Housing:**
- Upgrading informal settlements
- Social housing programs
- Land tenure security

**Infrastructure:**
- Water supply improvements
- Sanitation projects
- Road construction
- Public transport

**Governance:**
- Urban planning capacity
- Revenue generation
- Community participation
- Regional coordination

### Case Study: Port Vila
- Population growth ~4% per year
- Informal settlements: Blacksands, Freswin
- Challenges: Water, waste, housing
- Solutions: Urban planning, infrastructure investment
`,
        keyTerms: ["urbanization", "informal settlement", "zoning", "urban planning", "infrastructure"],
        practiceQuestions: [
          { q: "What are the main causes of urbanization?", a: "Pull factors (jobs, services, lifestyle) and push factors (rural poverty, land shortage, disasters)" },
          { q: "What are informal settlements?", a: "Unplanned housing areas often lacking proper services, infrastructure, and legal land tenure" },
          { q: "What urban challenges does Port Vila face?", a: "Rapid population growth, informal settlements, water supply, waste management, and housing" }
        ],
        examTips: "Know Pacific urban examples well. Be able to suggest practical solutions."
      },
      {
        id: "unit2.3",
        title: "Tourism: Effects, Planning and Management",
        content: `
## Tourism Effects

### Positive Effects

**Economic:**
- Foreign exchange earnings
- Employment creation
- Infrastructure development
- Business opportunities

**Social:**
- Cultural exchange
- Skills development
- Community pride
- Service improvements

**Environmental:**
- Conservation funding
- Protected area support
- Environmental awareness

### Negative Effects

**Economic:**
- Leakage (profits leaving)
- Dependency on tourism
- Inflation
- Seasonal employment

**Social:**
- Cultural erosion
- Social disruption
- Crime increase
- Inequality

**Environmental:**
- Reef damage
- Pollution
- Water consumption
- Waste generation

## Tourism Planning

### Sustainable Tourism

**Principles:**
- Environmental sustainability
- Economic viability
- Social acceptability
- Cultural preservation

**Strategies:**
- Carrying capacity limits
- Ecotourism development
- Community-based tourism
- Certification programs

### Pacific Tourism Management

**Regional Cooperation:**
- South Pacific Tourism Organisation
- Marketing collaboration
- Skill sharing

**National Policies:**
- Tourism master plans
- Protected area management
- Cultural tourism guidelines
- Environmental regulations

### Case Study: Vanuatu Tourism
- Pre-COVID: ~120,000 visitors/year
- Cruise ships significant
- Key attractions: Volcanoes, culture, diving
- Challenges: Infrastructure, distribution of benefits
- Solutions: Community-based tourism, diversification
`,
        keyTerms: ["sustainable tourism", "leakage", "carrying capacity", "ecotourism", "community-based tourism"],
        practiceQuestions: [
          { q: "What is tourism leakage?", a: "When tourism revenue leaves the local economy through foreign-owned businesses, imported goods, or foreign workers" },
          { q: "What is carrying capacity in tourism?", a: "The maximum number of tourists an area can support without negative impacts on the environment, society, or economy" },
          { q: "What are the benefits of community-based tourism?", a: "Direct benefits to local communities, cultural preservation, local ownership, and more authentic experiences" }
        ],
        examTips: "Balance positive and negative effects in essays. Use specific Pacific examples."
      }
    ]
  },
  strand3: {
    title: "Local, Regional and Global Studies",
    overview: "Pacific geography, climate change, and regional issues",
    image: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Geomorphology and Geology of the Pacific Islands",
        content: `
## Pacific Island Types

### High Islands (Volcanic)

**Characteristics:**
- Mountainous terrain
- Volcanic origin
- Rich soils
- Freshwater streams
- Larger land area

**Examples:**
- Vanuatu islands
- Fiji (Viti Levu)
- Samoa
- Solomon Islands

### Low Islands (Atolls)

**Characteristics:**
- Coral reef formation
- Low elevation (<5m)
- Limited freshwater
- Nutrient-poor soils
- Small land area

**Examples:**
- Marshall Islands
- Kiribati
- Tuvalu
- Parts of FSM

### Raised Coral Islands

**Characteristics:**
- Uplifted coral reefs
- Higher elevation than atolls
- Limestone rock
- Limited fresh water

**Examples:**
- Niue
- Nauru
- Parts of Vanuatu

## Pacific Geology

### Tectonic Setting
- Pacific Plate dominant
- Ring of Fire surrounds
- Multiple plate boundaries
- Active volcanism

### Island Formation

**Volcanic Islands:**
1. Underwater volcanic activity
2. Lava builds up
3. Island emerges above sea
4. Erosion and reef growth

**Coral Atolls:**
1. Volcanic island forms
2. Coral reef grows around
3. Volcano subsides
4. Only reef remains

### Pacific Diversity
- Thousands of islands
- Varied sizes and types
- Different resources
- Unique ecosystems
`,
        keyTerms: ["high island", "low island", "atoll", "coral reef", "volcanic arc", "plate boundary"],
        practiceQuestions: [
          { q: "What are the main differences between high islands and atolls?", a: "High islands are volcanic, mountainous, have fresh water and rich soils; atolls are low coral formations with limited freshwater and poor soils" },
          { q: "How do coral atolls form?", a: "A volcanic island forms, coral reef grows around it, the volcano subsides, leaving only the coral ring" },
          { q: "Why is the Pacific Ocean geologically active?", a: "It is surrounded by the Ring of Fire with multiple plate boundaries and subduction zones" }
        ],
        examTips: "Know the characteristics of each island type and be able to draw formation diagrams."
      },
      {
        id: "unit3.2",
        title: "Climate and Climate Change in the Pacific Region",
        content: `
## Pacific Climate

### Climate Characteristics
- Tropical maritime climate
- Warm year-round (25-30°C)
- High humidity
- Seasonal rainfall variation
- Trade wind influence

### Climate Variations
- El Niño: Drier in western Pacific
- La Niña: Wetter in western Pacific
- Inter-annual variability high

## Climate Change Impacts

### Observed Changes
- Temperature increase (~0.1°C per decade)
- Sea level rise (~3mm per year)
- Ocean acidification
- Changing rainfall patterns
- More intense cyclones

### Pacific Vulnerabilities

**Sea Level Rise:**
- Coastal flooding
- Saltwater intrusion
- Erosion
- Island disappearance risk (atolls)

**Extreme Events:**
- More intense cyclones
- Flooding and droughts
- Coral bleaching events

**Food Security:**
- Crop damage
- Fisheries changes
- Water shortages

### Adaptation Strategies

**Coastal Protection:**
- Seawalls and barriers
- Mangrove restoration
- Setback requirements
- Relocation planning

**Water Security:**
- Rainwater harvesting
- Groundwater protection
- Desalination
- Water conservation

**Agriculture:**
- Salt-tolerant crops
- Diversification
- Food storage
- Traditional knowledge

**Community-based:**
- Local adaptation plans
- Traditional practices
- Ecosystem-based adaptation
- Regional cooperation

### Pacific Climate Action
- Pacific Island Forum advocacy
- Loss and damage negotiations
- Climate finance access
- Regional cooperation
`,
        keyTerms: ["climate change", "sea level rise", "adaptation", "ocean acidification", "coral bleaching"],
        practiceQuestions: [
          { q: "What are the main climate change impacts on Pacific islands?", a: "Sea level rise, more intense cyclones, coral bleaching, changed rainfall patterns, and ocean acidification" },
          { q: "Why are low-lying atolls particularly vulnerable?", a: "Their low elevation makes them susceptible to sea level rise, flooding, and saltwater intrusion" },
          { q: "Name three climate adaptation strategies for Pacific islands.", a: "Coastal protection (seawalls, mangroves), water security measures, and salt-tolerant crops" }
        ],
        examTips: "Be specific about impacts and adaptations. Know Pacific advocacy positions."
      },
      {
        id: "unit3.3",
        title: "Pacific Island Territories and Political Issues",
        content: `
## Pacific Cultural Regions

### Melanesia
- **Countries**: PNG, Solomon Islands, Vanuatu, Fiji, New Caledonia
- **Characteristics**: Diverse languages, complex societies, larger islands

### Polynesia
- **Countries**: Samoa, Tonga, Tuvalu, Cook Islands, French Polynesia
- **Characteristics**: Hierarchical societies, ocean navigation, cultural links

### Micronesia
- **Countries**: FSM, Palau, Marshall Islands, Kiribati, Nauru
- **Characteristics**: Small islands, maritime traditions, diverse governance

## Political Status

### Independent Nations
- Papua New Guinea (1975)
- Solomon Islands (1978)
- Vanuatu (1980)
- Fiji (1970)
- Samoa (1962)
- Tonga (never colonized)

### Territories and Dependencies
- French: New Caledonia, French Polynesia
- US: Guam, American Samoa, CNMI
- NZ: Cook Islands, Niue (free association)

### Compact of Free Association
- FSM, Marshall Islands, Palau with USA
- Independence with defense agreement
- Financial assistance

## Political Issues

### Decolonization
- New Caledonia independence referendums
- West Papua self-determination
- Regional solidarity

### Regional Cooperation
- Pacific Islands Forum
- Melanesian Spearhead Group
- Polynesian Leaders Group
- Pacific Community

### Geopolitical Competition
- Traditional partners (Australia, NZ, USA)
- China increasing engagement
- Infrastructure and aid
- Strategic concerns
`,
        keyTerms: ["Melanesia", "Polynesia", "Micronesia", "Pacific Islands Forum", "Compact of Free Association"],
        practiceQuestions: [
          { q: "What are the three cultural regions of the Pacific?", a: "Melanesia, Polynesia, and Micronesia" },
          { q: "Which Pacific territories are still under French administration?", a: "New Caledonia and French Polynesia" },
          { q: "What is the Pacific Islands Forum?", a: "The main regional organization for Pacific island nations, addressing political, economic, and environmental issues" }
        ],
        examTips: "Know which countries belong to each region. Understand current political issues."
      },
      {
        id: "unit3.4",
        title: "Pacific Island Economies and Challenges",
        content: `
## Pacific Economic Characteristics

### Common Features
- Small domestic markets
- Remote locations
- High transport costs
- Limited resources
- Aid dependency

### Economic Sectors

**Agriculture:**
- Subsistence farming
- Cash crops (copra, cocoa, palm oil)
- Limited arable land

**Fisheries:**
- Large EEZs
- Tuna fishing (licensing)
- Coastal fisheries
- Aquaculture potential

**Tourism:**
- Major earner for some
- Cruise ships
- Cultural tourism
- Diving and beaches

**Remittances:**
- Workers abroad send money home
- Major income source (Samoa, Tonga)
- Seasonal worker programs

## Economic Challenges

### Structural Issues
- MIRAB economy (Migration, Remittances, Aid, Bureaucracy)
- Trade deficits
- Limited exports
- Vulnerability to shocks

### External Factors
- Global commodity prices
- Climate change impacts
- COVID-19 pandemic effects
- Shipping costs

### Development Challenges
- Infrastructure gaps
- Human capital limitations
- Governance issues
- Land tenure complexity

## Economic Solutions

### Diversification
- Develop multiple sectors
- Reduce dependency
- Value-added processing

### Regional Integration
- Pacific Agreement on Closer Economic Relations (PACER)
- Trade facilitation
- Labor mobility

### Sustainable Development
- Blue economy (ocean resources)
- Ecotourism
- Renewable energy
- Digital connectivity

### Case Study: Vanuatu Economy
- GDP ~$900 million
- Agriculture, tourism, offshore banking
- Challenges: Infrastructure, climate vulnerability
- Opportunities: Tourism growth, kava exports, fisheries
`,
        keyTerms: ["MIRAB economy", "remittances", "EEZ", "blue economy", "economic diversification"],
        practiceQuestions: [
          { q: "What does MIRAB stand for?", a: "Migration, Remittances, Aid, and Bureaucracy - describing many Pacific island economies" },
          { q: "Why are Pacific island economies vulnerable?", a: "Small markets, remote locations, limited resources, and dependency on few sectors" },
          { q: "What is the blue economy?", a: "Sustainable use of ocean resources for economic growth, jobs, and ocean health" }
        ],
        examTips: "Use economic data and examples. Understand both challenges and solutions."
      },
      {
        id: "unit3.5",
        title: "SPREP and Conservation Issues in the Pacific",
        content: `
## SPREP Overview

### What is SPREP?
- Secretariat of the Pacific Regional Environment Programme
- Based in Apia, Samoa
- 26 member countries and territories
- Established 1993

### SPREP's Mission
"To promote cooperation in the Pacific region and provide assistance to protect and improve the environment and ensure sustainable development"

### Key Focus Areas
1. Climate change resilience
2. Ecosystem and biodiversity protection
3. Waste management and pollution control
4. Environmental monitoring and governance

## Pacific Conservation Issues

### Marine Conservation

**Threats:**
- Overfishing
- Illegal fishing
- Pollution
- Coral bleaching
- Coastal development

**Solutions:**
- Marine protected areas
- Sustainable fishing practices
- Regional fisheries management
- Coral reef monitoring

### Terrestrial Conservation

**Threats:**
- Deforestation
- Invasive species
- Mining impacts
- Agriculture expansion

**Solutions:**
- Protected areas
- Invasive species control
- Sustainable forestry
- Community conservation

### Species Protection

**Endangered Species:**
- Sea turtles
- Dugongs
- Many bird species
- Coconut crabs

**Conservation Actions:**
- Nesting site protection
- Hunting regulations
- Habitat restoration
- Public awareness

## Regional Conservation Programs

### Micronesia Challenge
- 30% of marine areas protected by 2020
- 20% of terrestrial areas protected

### Coral Triangle Initiative
- Six countries cooperation
- Marine biodiversity focus

### Pacific Islands Roundtable
- Coordination mechanism
- Sustainable development focus

### Traditional Conservation
- Tabu (temporary closures)
- Customary marine tenure
- Sacred sites
- Traditional knowledge
`,
        keyTerms: ["SPREP", "marine protected area", "invasive species", "coral reef", "traditional conservation"],
        practiceQuestions: [
          { q: "What is SPREP and what is its role?", a: "The Secretariat of the Pacific Regional Environment Programme - it coordinates regional environmental protection and sustainable development" },
          { q: "What are the main threats to Pacific marine ecosystems?", a: "Overfishing, illegal fishing, pollution, coral bleaching, and coastal development" },
          { q: "How do traditional conservation methods work?", a: "Through practices like tabu (temporary harvest closures), customary marine tenure, and protection of sacred sites" }
        ],
        examTips: "Know SPREP's work and regional programs. Link traditional and modern conservation."
      }
    ]
  },
  strand4: {
    title: "Geographical Skills and Ideas",
    overview: "Advanced mapping, research, and geographic analysis skills",
    image: "https://images.unsplash.com/photo-1524661135-423995f22d0b?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Interpretation of Relief and Land Use from Topographic Maps",
        content: `
## Topographic Maps

### Understanding Contour Lines
- Show elevation above sea level
- Contour interval: vertical distance between lines
- Index contours: labeled, thicker lines

### Relief Identification

**Steep Slopes:**
- Closely spaced contours
- Cliffs shown by merged lines

**Gentle Slopes:**
- Widely spaced contours
- Gradual elevation change

**Valleys:**
- V-shaped contours pointing uphill
- Rivers in valley bottom

**Ridges:**
- V-shaped contours pointing downhill
- High ground between valleys

**Hills:**
- Concentric circles
- Highest point at center

### Land Use Interpretation

**Agricultural Areas:**
- Field patterns
- Irrigation systems
- Farm buildings

**Urban Areas:**
- Dense building patterns
- Road networks
- Named settlements

**Industrial Areas:**
- Large buildings
- Transport connections
- Distinctive features

**Forested Areas:**
- Green coloring
- Tree symbols

### Cross-Section Drawing

**Steps:**
1. Draw baseline at correct length
2. Mark elevation points
3. Plot heights using scale
4. Connect points smoothly
5. Add labels
`,
        keyTerms: ["contour line", "contour interval", "relief", "topographic map", "cross-section"],
        practiceQuestions: [
          { q: "How can you identify a steep slope on a topographic map?", a: "By closely spaced contour lines" },
          { q: "What direction do contour V-shapes point in a valley?", a: "Uphill (towards higher elevation)" },
          { q: "What is a contour interval?", a: "The vertical distance between consecutive contour lines on a map" }
        ],
        examTips: "Practice cross-sections until confident. Know common map symbols."
      },
      {
        id: "unit4.2",
        title: "Advanced Mapping Skills and Map Interpretation",
        content: `
## Advanced Map Types

### Geological Maps
- Show rock types
- Indicate geological structures
- Fault lines marked
- Age of rocks shown by color

### Aerial Photographs

**Types:**
- Vertical (looking straight down)
- Oblique (looking at an angle)

**Interpretation:**
- Scale varies with height
- Shadows show features
- Stereoscopic pairs for 3D

### Satellite Imagery
- Multiple spectral bands
- Large area coverage
- Time series analysis
- Digital processing

## Advanced Skills

### Map Comparison
- Same area, different times
- Identify changes
- Analyze development

### Statistical Mapping
- Choropleth techniques
- Proportional symbols
- Isoline interpolation
- Dot distribution

### GIS Concepts
- Layers of information
- Spatial analysis
- Digital mapping
- Data integration

### Field Mapping
- Sketch mapping
- Accurate measurements
- Feature recording
- Map updating

## Interpretation Techniques

### SCQIP Analysis
S - Size and scale
C - Color and symbols
Q - Questions to answer
I - Information provided
P - Patterns observed
`,
        keyTerms: ["geological map", "aerial photograph", "satellite imagery", "GIS", "choropleth"],
        practiceQuestions: [
          { q: "What information do geological maps show?", a: "Rock types, geological structures, fault lines, and the age of rock formations" },
          { q: "What is the difference between vertical and oblique aerial photographs?", a: "Vertical photos look straight down; oblique photos look at an angle" },
          { q: "What is GIS?", a: "Geographic Information System - software for creating, managing, analyzing, and mapping spatial data" }
        ],
        examTips: "Practice interpreting different map types. Know how to compare maps of different dates."
      },
      {
        id: "unit4.3",
        title: "Practical Skills and Key Geographic Ideas",
        content: `
## Data Presentation Skills

### Advanced Graphs

**Scatter Graphs:**
- Show correlation
- Line of best fit
- Identify outliers

**Triangular Graphs:**
- Three variables totaling 100%
- Used for soil, employment data

**Dispersion Diagrams:**
- Show distribution spread
- Median and quartiles

**Logarithmic Graphs:**
- Show exponential change
- Compare different scales

### Statistical Analysis

**Measures of Central Tendency:**
- Mean (average)
- Median (middle value)
- Mode (most common)

**Measures of Dispersion:**
- Range
- Interquartile range
- Standard deviation

**Correlation:**
- Positive, negative, or none
- Spearman's rank correlation

### Visual Resources

**Photograph Analysis:**
1. Describe what you see
2. Identify geographic features
3. Analyze patterns and processes
4. Link to geographic concepts

**Cartoon Analysis:**
1. Identify the message
2. Analyze symbols used
3. Consider the perspective
4. Evaluate effectiveness

### Key Geographic Concepts

**Spatial Concepts:**
- Location and distribution
- Pattern and process
- Scale and connections
- Change over time

**Human-Environment Interaction:**
- Resource use
- Environmental impact
- Sustainability
- Adaptation
`,
        keyTerms: ["correlation", "mean", "median", "standard deviation", "spatial analysis"],
        practiceQuestions: [
          { q: "What is the difference between mean, median, and mode?", a: "Mean is the average, median is the middle value, mode is the most common value" },
          { q: "What type of graph would you use to show three variables totaling 100%?", a: "A triangular graph" },
          { q: "What does a positive correlation show?", a: "That as one variable increases, the other also increases" }
        ],
        examTips: "Practice statistical calculations. Know when to use each graph type."
      },
      {
        id: "unit4.4",
        title: "Communication and Research Skills",
        content: `
## Research Design

### Research Process

**1. Define the Problem:**
- Clear research question
- Hypothesis if applicable
- Scope and limitations

**2. Research Design:**
- Methods selection
- Sampling strategy
- Data collection plan
- Timeline

**3. Data Collection:**
- Primary sources
- Secondary sources
- Fieldwork
- Surveys/interviews

**4. Data Analysis:**
- Processing data
- Statistical analysis
- Pattern identification
- Drawing conclusions

**5. Presentation:**
- Report writing
- Visual presentation
- Oral presentation
- Publication

### Fieldwork Methods

**Observation:**
- Systematic recording
- Sampling techniques
- Photography

**Measurement:**
- Physical measurements
- Environmental monitoring
- Traffic counts

**Surveys:**
- Questionnaire design
- Interview techniques
- Sampling methods

**Mapping:**
- Land use mapping
- Environmental survey
- GPS use

## Communication Skills

### Written Reports

**Structure:**
- Title and abstract
- Introduction
- Methods
- Results
- Discussion
- Conclusion
- References

### Visual Communication
- Clear diagrams
- Appropriate graphs
- Maps and photos
- Digital presentations

### Oral Presentation
- Clear structure
- Visual aids
- Time management
- Audience engagement

### Referencing
- In-text citations
- Reference list
- Avoiding plagiarism
- Source evaluation
`,
        keyTerms: ["hypothesis", "sampling", "fieldwork", "research design", "methodology"],
        practiceQuestions: [
          { q: "What are the main steps in the research process?", a: "Define the problem, design research, collect data, analyze data, present findings" },
          { q: "What is the purpose of a hypothesis?", a: "To make a testable prediction that guides the research" },
          { q: "What should be included in a research report?", a: "Title, abstract, introduction, methods, results, discussion, conclusion, and references" }
        ],
        examTips: "Practice structuring research projects. Know proper referencing methods."
      }
    ]
  }
};

// Year 13 Geography Student Notes
export const year13GeographyStudentNotes = {
  strand1: {
    title: "Natural Processes",
    overview: "Advanced analysis of Earth's forces and landform development",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Earth's Internal and External Forces",
        content: `
## Internal Forces (Endogenic)

### Types of Internal Forces

**Tectonic Forces:**
- Compression (fold mountains)
- Tension (rift valleys)
- Shearing (transform faults)

**Volcanic Forces:**
- Magma movement
- Eruption processes
- Intrusive formations

**Seismic Forces:**
- Earthquake energy
- Wave propagation
- Ground deformation

### Landforms from Internal Forces
- Mountains (fold, block, volcanic)
- Plateaus and plains
- Rift valleys
- Volcanic islands
- Fault scarps

## External Forces (Exogenic)

### Types of External Forces

**Weathering:**
- Physical breakdown
- Chemical alteration
- Biological action

**Erosion:**
- Water (fluvial)
- Wind (aeolian)
- Ice (glacial)
- Waves (coastal)

**Mass Movement:**
- Landslides
- Rockfalls
- Creep
- Mudflows

### Deposition
- River deltas
- Beaches
- Dunes
- Alluvial fans

## Force Interactions

### Dynamic Equilibrium
- Balance between building and erosion
- Landscape constantly changing
- Human impacts on balance

### Systems Approach
- Inputs, processes, outputs
- Feedback mechanisms
- Energy flows
- Material cycles
`,
        keyTerms: ["endogenic", "exogenic", "tectonic", "dynamic equilibrium", "systems approach"],
        practiceQuestions: [
          { q: "What is the difference between endogenic and exogenic forces?", a: "Endogenic forces originate from within the Earth; exogenic forces act on the Earth's surface from outside" },
          { q: "What is dynamic equilibrium in geography?", a: "The balance between forces building up landforms and forces eroding them, resulting in a constantly adjusting landscape" },
          { q: "Name three types of mass movement.", a: "Landslides, rockfalls, and mudflows (or creep)" }
        ],
        examTips: "Use systems diagrams to show process interactions. Link to specific case studies."
      },
      {
        id: "unit1.2",
        title: "Tectonic, Volcanic, Fluvial or Coastal Processes",
        content: `
## Tectonic Processes

### Plate Boundary Processes

**Convergent:**
- Subduction
- Mountain building
- Volcanic arcs
- Deep sea trenches

**Divergent:**
- Rifting
- New crust formation
- Mid-ocean ridges

**Transform:**
- Horizontal movement
- Fault zones
- Earthquakes

### Landform Development
- Fold mountains evolve over millions of years
- Volcanic islands grow and erode
- Continents reshape

## Volcanic Processes

### Magma Dynamics
- Composition affects eruption style
- Viscosity determines flow
- Gas content influences explosivity

### Eruption Types
- Effusive: Gentle lava flows
- Explosive: Violent eruptions
- Phreatic: Steam explosions

### Volcanic Landforms
- Calderas
- Lava domes
- Volcanic necks
- Hot springs

## Fluvial Processes

### River System Dynamics
- Drainage basin as a system
- Energy and sediment budgets
- Equilibrium concepts

### Process Interactions
- Erosion-transportation-deposition cycle
- Base level changes
- Channel adjustment

### Landform Evolution
- Valley development
- Meander migration
- Delta growth

## Coastal Processes

### Wave Dynamics
- Wave generation
- Wave refraction
- Wave energy distribution

### Coastal System
- Sediment budget
- Longshore drift
- Beach profiles

### Coastal Change
- Erosion rates
- Accretion patterns
- Sea level influence
`,
        keyTerms: ["subduction", "base level", "sediment budget", "wave refraction", "longshore drift"],
        practiceQuestions: [
          { q: "How does magma composition affect volcanic eruptions?", a: "More silica-rich magma is more viscous and causes more explosive eruptions; basaltic magma flows more easily" },
          { q: "What is base level in fluvial geography?", a: "The lowest level to which a river can erode, usually sea level" },
          { q: "How does longshore drift work?", a: "Waves approach at an angle, moving sediment along the coast in a zigzag pattern" }
        ],
        examTips: "Choose one process type for deep study. Use detailed case studies and diagrams."
      },
      {
        id: "unit1.3",
        title: "Cyclonic and Aeolian Processes",
        content: `
## Cyclonic Processes

### Tropical Cyclone Dynamics

**Formation:**
1. Warm ocean water (>26°C)
2. Atmospheric instability
3. Coriolis effect
4. Low vertical wind shear
5. Organized convection

**Structure:**
- Eye: Calm center (20-50km)
- Eye wall: Strongest winds
- Spiral rainbands: Outer precipitation
- Outflow: Upper level divergence

### Cyclone Impacts

**Primary Hazards:**
- Extreme winds (>250km/h possible)
- Storm surge
- Intense rainfall

**Secondary Hazards:**
- Flooding
- Landslides
- Wave damage

### Cyclone Climate Patterns
- Seasonal occurrence
- Climate change effects
- Intensity trends

## Aeolian Processes

### Wind Erosion

**Mechanisms:**
- Deflation (lifting particles)
- Abrasion (sandblasting)
- Attrition (particle collision)

**Conditions for Erosion:**
- Strong winds
- Dry conditions
- Loose sediment
- Little vegetation

### Wind Transportation

**Methods:**
- Suspension (fine particles)
- Saltation (bouncing)
- Surface creep (rolling)

### Deposition

**Dune Types:**
- Barchan: Crescent-shaped
- Linear: Long parallel ridges
- Star: Multiple arms
- Transverse: Perpendicular to wind

**Other Features:**
- Loess deposits
- Sand sheets
- Desert pavement

### Aeolian Environments
- Hot deserts
- Coastal dunes
- Cold deserts
- Periglacial areas

## Process Interactions
- Cyclones redistribute sediment
- Wind follows cyclone damage
- Coastal-aeolian links
`,
        keyTerms: ["eye wall", "storm surge", "deflation", "saltation", "barchan dune", "loess"],
        practiceQuestions: [
          { q: "What conditions are needed for tropical cyclone formation?", a: "Warm ocean water (>26°C), atmospheric instability, Coriolis effect, low wind shear, and organized convection" },
          { q: "What is saltation?", a: "The movement of sand particles by bouncing along the surface, propelled by wind" },
          { q: "Name three types of sand dunes.", a: "Barchan (crescent), linear (parallel ridges), and star (multiple arms)" }
        ],
        examTips: "Link cyclonic processes to Pacific examples. Understand aeolian processes in different environments."
      }
    ]
  },
  strand2: {
    title: "Cultural Processes",
    overview: "Advanced analysis of migration, economic change, and tourism systems",
    image: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Population Movement: Migration",
        content: `
## Migration Theory

### Lee's Migration Model
- Origin (push factors)
- Destination (pull factors)
- Intervening obstacles
- Personal factors

### Types of Migration

**By Distance:**
- Internal (within country)
- International (between countries)
- Regional (within region)

**By Duration:**
- Permanent
- Temporary
- Seasonal
- Circular

**By Choice:**
- Voluntary
- Forced (refugees, displaced)
- Reluctant

## Migration Impacts

### Sending Areas

**Positives:**
- Remittances
- Reduced pressure on resources
- Skills transfer on return

**Negatives:**
- Brain drain
- Aging population
- Social disruption

### Receiving Areas

**Positives:**
- Labor supply
- Cultural diversity
- Economic growth

**Negatives:**
- Service pressure
- Social tensions
- Integration challenges

## Pacific Migration

### Patterns
- Rural to urban
- Island to mainland
- Pacific to Rim countries

### Case Study: Seasonal Worker Programs
- Australia and New Zealand schemes
- Benefits: Remittances, skills
- Challenges: Family separation, exploitation risk
- Pacific impact: Significant income source

### Climate Migration
- Emerging concern
- Low-lying island populations
- Planned relocation considerations
- Regional implications
`,
        keyTerms: ["push factors", "pull factors", "remittances", "brain drain", "circular migration"],
        practiceQuestions: [
          { q: "What are the components of Lee's migration model?", a: "Origin (push factors), destination (pull factors), intervening obstacles, and personal factors" },
          { q: "What is brain drain?", a: "The emigration of skilled and educated people from their home country to another" },
          { q: "How do seasonal worker programs benefit Pacific island countries?", a: "Through remittances, skills development, and employment for workers who might otherwise be unemployed" }
        ],
        examTips: "Use migration theories to analyze case studies. Consider both sending and receiving area impacts."
      },
      {
        id: "unit2.2",
        title: "Industrialization or Agricultural Changes",
        content: `
## Industrial Geography

### Industrial Location Theory

**Weber's Model:**
- Transport costs
- Labor costs
- Agglomeration benefits

**Modern Factors:**
- Market access
- Technology
- Government policy
- Labor skills
- Infrastructure

### Industrial Change

**Deindustrialization:**
- Manufacturing decline
- Service sector growth
- Automation impacts
- Global shifts

**Industrialization:**
- Manufacturing growth
- Urbanization
- Environmental impacts
- Economic transformation

## Agricultural Geography

### Agricultural Systems

**Subsistence:**
- Family consumption
- Traditional methods
- Limited surplus

**Commercial:**
- Market-oriented
- Mechanized
- Specialized

### Agricultural Change

**Green Revolution:**
- High-yield varieties
- Irrigation
- Fertilizers/pesticides
- Mixed results

**Sustainable Agriculture:**
- Organic methods
- Agroforestry
- Conservation farming
- Traditional knowledge

## Pacific Context

### Agricultural Changes
- Cash crop introduction
- Market integration
- Climate impacts
- Land tenure issues

### Limited Industrialization
- Small markets
- High costs
- Processing opportunities
- Service sector focus

### Case Study: Vanuatu Agriculture
- Traditional: Root crops, subsistence
- Commercial: Copra, cocoa, kava, beef
- Challenges: Market access, climate, pests
- Opportunities: Organic certification, niche markets
`,
        keyTerms: ["deindustrialization", "Green Revolution", "subsistence", "commercial agriculture", "agglomeration"],
        practiceQuestions: [
          { q: "What factors does Weber's industrial location theory consider?", a: "Transport costs, labor costs, and agglomeration benefits" },
          { q: "What is the Green Revolution?", a: "The introduction of high-yield crop varieties, irrigation, and chemical inputs that increased agricultural productivity, especially in developing countries" },
          { q: "What agricultural opportunities exist in Vanuatu?", a: "Organic certification, niche markets for kava and other products, and value-added processing" }
        ],
        examTips: "Choose either industrial or agricultural focus. Use specific examples and data."
      },
      {
        id: "unit2.3",
        title: "Tourism",
        content: `
## Tourism Systems

### Butler's Tourism Area Life Cycle

**Stages:**
1. Exploration: Few tourists, minimal impact
2. Involvement: Local involvement begins
3. Development: Rapid tourism growth
4. Consolidation: Growth slows, tourism dominant
5. Stagnation: Carrying capacity reached
6. Decline or Rejuvenation: Future depends on management

### Tourism Typology

**Mass Tourism:**
- Large numbers
- Package tours
- Concentrated areas

**Alternative Tourism:**
- Ecotourism
- Cultural tourism
- Adventure tourism
- Community-based

## Tourism Impacts

### Economic Impacts
- Employment multiplier
- Foreign exchange
- Infrastructure development
- Leakage and dependency

### Social Impacts
- Demonstration effect
- Cultural commodification
- Community disruption
- Empowerment potential

### Environmental Impacts
- Carrying capacity limits
- Ecosystem degradation
- Resource consumption
- Conservation potential

## Sustainable Tourism

### Principles
- Environmental sustainability
- Economic viability
- Social acceptability
- Cultural preservation

### Management Strategies
- Zoning and access control
- Certification schemes
- Community involvement
- Monitoring and evaluation

## Pacific Tourism

### Industry Characteristics
- Natural attractions
- Cultural experiences
- Cruise tourism
- Resort development

### Challenges
- Infrastructure limitations
- Climate vulnerability
- Benefit distribution
- Seasonal variation

### Opportunities
- Ecotourism development
- Cultural tourism
- Dive tourism
- Adventure activities
`,
        keyTerms: ["Butler model", "carrying capacity", "ecotourism", "demonstration effect", "multiplier effect"],
        practiceQuestions: [
          { q: "What are the stages of Butler's Tourism Area Life Cycle?", a: "Exploration, involvement, development, consolidation, stagnation, and decline or rejuvenation" },
          { q: "What is the demonstration effect in tourism?", a: "When local people change their behavior or aspirations to imitate tourists" },
          { q: "What is tourism carrying capacity?", a: "The maximum number of tourists an area can accommodate without negative impacts on environment, society, or economy" }
        ],
        examTips: "Apply Butler's model to specific destinations. Balance positive and negative impacts in analysis."
      }
    ]
  },
  strand3: {
    title: "Local, Regional and Global Studies",
    overview: "Climate change, global issues, and international environmental governance",
    image: "https://images.unsplash.com/photo-1569163139599-0f4517e36f51?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Advanced Geomorphology",
        content: `
## Landform Systems

### Process-Form Relationships
- How processes create landforms
- Feedback mechanisms
- Equilibrium concepts

### Scale Considerations
- Micro: Individual features
- Meso: Landscape components
- Macro: Regional patterns

## Case Study Analysis

### Volcanic Landforms
- Vanuatu's volcanic arc
- Eruption styles and products
- Hazard assessment
- Monitoring systems

### Coastal Landforms
- Pacific island coastlines
- Reef systems
- Sea level changes
- Human modifications

### Fluvial Landforms
- Pacific island rivers
- Tropical erosion rates
- Sediment transport
- Delta formation

## Applied Geomorphology

### Hazard Assessment
- Mapping hazard zones
- Risk evaluation
- Mitigation planning

### Resource Management
- Aggregate extraction
- Coastal protection
- Land capability

### Environmental Impact
- Development effects
- Climate change impacts
- Restoration potential
`,
        keyTerms: ["process-form relationship", "equilibrium", "hazard assessment", "land capability"],
        practiceQuestions: [
          { q: "What is meant by process-form relationships in geomorphology?", a: "The connection between geomorphic processes (erosion, deposition) and the landforms they create" },
          { q: "How is geomorphology applied to hazard assessment?", a: "By mapping hazard zones, evaluating risks, and planning mitigation based on landform analysis" }
        ],
        examTips: "Focus on application of geomorphology to real-world problems."
      },
      {
        id: "unit3.2",
        title: "Issues Relating to Climate Change",
        content: `
## Climate Change Science

### Evidence
- Temperature records
- Sea level measurements
- Ice core data
- Ecosystem changes

### Causes
- Greenhouse gas emissions
- Land use change
- Feedback mechanisms

### Projections
- Temperature increase
- Sea level rise
- Extreme events
- Regional variations

## Global Impacts

### Physical Impacts
- Ice sheet melting
- Sea level rise
- Ocean acidification
- Weather pattern changes

### Human Impacts
- Food security
- Water availability
- Health effects
- Displacement

### Economic Impacts
- Infrastructure damage
- Agricultural changes
- Insurance costs
- Adaptation spending

## Pacific Climate Impacts

### Specific Vulnerabilities
- Low-lying islands
- Coastal communities
- Marine resources
- Freshwater supplies

### Observed Changes
- Sea level rise (3-4mm/year)
- Coral bleaching events
- Cyclone intensity
- Drought frequency

## Response Strategies

### Mitigation
- Emissions reduction
- Renewable energy
- Carbon sequestration
- International agreements

### Adaptation
- Infrastructure protection
- Ecosystem-based approaches
- Early warning systems
- Planned relocation

### Pacific Action
- Climate advocacy
- Adaptation planning
- Regional cooperation
- Loss and damage claims
`,
        keyTerms: ["mitigation", "adaptation", "greenhouse gas", "ocean acidification", "loss and damage"],
        practiceQuestions: [
          { q: "What is the difference between climate change mitigation and adaptation?", a: "Mitigation reduces greenhouse gas emissions to slow climate change; adaptation adjusts to its impacts" },
          { q: "What are the main climate change impacts on Pacific islands?", a: "Sea level rise, coral bleaching, changed cyclone patterns, and threats to freshwater supplies" },
          { q: "What is ocean acidification?", a: "The decrease in ocean pH caused by absorption of atmospheric CO2, threatening marine life especially corals" }
        ],
        examTips: "Use current data and examples. Understand Pacific perspectives and advocacy."
      },
      {
        id: "unit3.3",
        title: "Global, Cultural and Political Issues",
        content: `
## Globalization

### Dimensions
- Economic globalization
- Cultural globalization
- Political globalization
- Environmental globalization

### Impacts
- Trade and investment
- Cultural exchange
- Governance challenges
- Environmental pressures

## Cultural Issues

### Cultural Change
- Traditional vs modern
- Language loss
- Identity questions
- Youth issues

### Cultural Preservation
- Heritage protection
- Language programs
- Traditional knowledge
- Community initiatives

## Political Issues

### Governance Challenges
- Small state capacity
- Regional cooperation
- International voice
- Development assistance

### Geopolitics
- Strategic competition
- Aid relationships
- Sovereignty concerns
- Regional organizations

## Global Issues in Pacific Context

### Economic Integration
- Trade agreements
- Foreign investment
- Regional markets
- Development models

### Cultural Dynamics
- Indigenous rights
- Cultural tourism
- Media influence
- Diaspora connections

### Political Engagement
- UN participation
- Climate advocacy
- Regional solidarity
- Self-determination
`,
        keyTerms: ["globalization", "sovereignty", "cultural preservation", "geopolitics", "regional cooperation"],
        practiceQuestions: [
          { q: "What are the four dimensions of globalization?", a: "Economic, cultural, political, and environmental globalization" },
          { q: "What cultural challenges do Pacific island nations face?", a: "Balancing traditional and modern values, language loss, identity questions, and youth issues" },
          { q: "How do Pacific island nations engage in global politics?", a: "Through UN participation, climate advocacy, regional organizations, and solidarity on key issues" }
        ],
        examTips: "Consider multiple perspectives on global issues. Link global trends to local impacts."
      },
      {
        id: "unit3.4",
        title: "Issues Relating to the World Economy",
        content: `
## Global Economic Systems

### International Trade
- Trade patterns
- Trade agreements
- Terms of trade
- Trade barriers

### Global Finance
- Foreign investment
- Aid flows
- Remittances
- Debt issues

### Economic Development
- Development models
- Inequality
- Sustainable development
- SDGs

## Pacific Economic Position

### Challenges
- Small economies
- Geographic isolation
- Limited resources
- Vulnerability to shocks

### Opportunities
- Ocean resources
- Tourism potential
- Niche exports
- Regional integration

### Development Strategies
- Economic diversification
- Regional cooperation
- Blue economy
- Digital connectivity

## Economic Issues

### Trade Dependency
- Export concentration
- Import reliance
- Terms of trade decline
- Market access

### Aid and Debt
- ODA dependency
- Debt sustainability
- Climate finance
- Development effectiveness

### Labor and Migration
- Unemployment
- Seasonal work
- Brain drain
- Remittance economy

## Future Directions

### Sustainable Economy
- Green growth
- Blue economy
- Circular economy
- Social enterprise

### Regional Integration
- Trade agreements
- Labor mobility
- Infrastructure sharing
- Joint ventures
`,
        keyTerms: ["terms of trade", "ODA", "blue economy", "SDGs", "economic diversification"],
        practiceQuestions: [
          { q: "What are the main economic challenges facing Pacific island nations?", a: "Small economies, geographic isolation, limited resources, and vulnerability to external shocks" },
          { q: "What is the blue economy?", a: "Sustainable use of ocean resources for economic growth, improved livelihoods, and ocean health" },
          { q: "What are the Sustainable Development Goals (SDGs)?", a: "17 global goals adopted by the UN in 2015 to end poverty, protect the planet, and ensure prosperity by 2030" }
        ],
        examTips: "Use economic data to support arguments. Understand Pacific perspectives on global economy."
      },
      {
        id: "unit3.5",
        title: "Treaties Relating to Biodiversity and Conservation",
        content: `
## International Environmental Agreements

### Convention on Biological Diversity (CBD)
- Adopted 1992
- Three objectives:
  1. Conservation of biodiversity
  2. Sustainable use
  3. Equitable benefit sharing
- Protocols: Cartagena (biosafety), Nagoya (access)

### CITES
- Convention on International Trade in Endangered Species
- Controls trade in wildlife
- Three appendices by threat level
- Pacific species covered

### UNEP
- UN Environment Programme
- Coordinates environmental activities
- Regional offices
- Scientific assessments

## Key Frameworks

### Ramsar Convention
- Wetlands of international importance
- Wise use principle
- Pacific sites included

### World Heritage Convention
- Natural and cultural sites
- Outstanding universal value
- Pacific sites: East Rennell, Rock Islands

### Paris Agreement
- Climate change
- 1.5-2°C target
- Nationally Determined Contributions
- Pacific advocacy

## Pacific Implementation

### Regional Programs
- SPREP coordination
- Species conservation
- Marine protection
- Capacity building

### Challenges
- Limited resources
- Monitoring capacity
- Enforcement
- Climate change

### Success Stories
- Marine protected areas
- Species recovery
- Community conservation
- Regional cooperation

## Traditional and International Law

### Integration
- Customary practices
- Indigenous rights
- Traditional knowledge
- Community management

### Examples
- Locally Managed Marine Areas
- Traditional protected areas
- Custom and conservation
`,
        keyTerms: ["CBD", "CITES", "UNEP", "Ramsar", "Paris Agreement", "Nagoya Protocol"],
        practiceQuestions: [
          { q: "What are the three objectives of the Convention on Biological Diversity?", a: "Conservation of biodiversity, sustainable use of its components, and fair and equitable sharing of benefits from genetic resources" },
          { q: "What is CITES and what does it regulate?", a: "The Convention on International Trade in Endangered Species - it regulates international trade in wildlife to prevent overexploitation" },
          { q: "How do Pacific islands implement international environmental agreements?", a: "Through regional programs via SPREP, marine protected areas, species conservation, and integration with traditional practices" }
        ],
        examTips: "Know key agreements and their Pacific relevance. Link international law to local implementation."
      }
    ]
  },
  strand4: {
    title: "Geographical Skills and Ideas",
    overview: "Advanced research, planning, and geographic decision-making skills",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Skills in Reading and Interpreting Geographical Resources",
        content: `
## Advanced Resource Interpretation

### Map Resources
- Topographic analysis
- Thematic interpretation
- Historical comparison
- Digital mapping

### Photographic Resources

**Ground Photos:**
- Perspective analysis
- Feature identification
- Change detection

**Aerial Photos:**
- Scale calculation
- Stereoscopic viewing
- Land use mapping

**Satellite Imagery:**
- Band combinations
- Change detection
- Classification

### Statistical Resources
- Tables and databases
- Time series data
- Spatial data
- Sampling issues

### Text Resources
- Academic sources
- Media analysis
- Document evaluation
- Critical reading

## Interpretation Techniques

### Multiple Source Analysis
- Triangulation
- Cross-referencing
- Verification

### Critical Evaluation
- Source reliability
- Bias identification
- Data quality
- Limitations

### Synthesis Skills
- Combining evidence
- Drawing conclusions
- Supporting arguments
- Presenting findings
`,
        keyTerms: ["triangulation", "stereoscopic", "classification", "synthesis", "critical evaluation"],
        practiceQuestions: [
          { q: "What is triangulation in geographic research?", a: "Using multiple sources or methods to verify information and increase reliability" },
          { q: "What should you consider when critically evaluating a source?", a: "Source reliability, potential bias, data quality, and limitations" }
        ],
        examTips: "Practice analyzing multiple resource types. Develop critical evaluation skills."
      },
      {
        id: "unit4.2",
        title: "Geographic Ideas in Planning and Decision-making",
        content: `
## Planning Principles

### Geographic Planning Concepts
- Spatial organization
- Land use allocation
- Resource management
- Environmental protection

### Planning Process
1. Problem identification
2. Data collection
3. Analysis
4. Option development
5. Evaluation
6. Implementation
7. Monitoring

## Decision-Making

### Geographic Decision Factors
- Location and accessibility
- Environmental capacity
- Social impacts
- Economic viability
- Political feasibility

### Decision-Making Tools

**Cost-Benefit Analysis:**
- Quantify costs and benefits
- Compare options
- Consider externalities

**Environmental Impact Assessment:**
- Predict impacts
- Propose mitigation
- Public consultation

**Multi-Criteria Analysis:**
- Multiple factors
- Weighted scoring
- Systematic comparison

### Stakeholder Analysis
- Identify stakeholders
- Assess interests
- Consider power dynamics
- Manage conflicts

## Application Areas

### Urban Planning
- Land use zoning
- Infrastructure planning
- Service provision
- Growth management

### Environmental Planning
- Protected areas
- Resource management
- Pollution control
- Rehabilitation

### Disaster Planning
- Risk assessment
- Preparedness planning
- Emergency response
- Recovery planning
`,
        keyTerms: ["cost-benefit analysis", "EIA", "multi-criteria analysis", "stakeholder", "zoning"],
        practiceQuestions: [
          { q: "What are the steps in the planning process?", a: "Problem identification, data collection, analysis, option development, evaluation, implementation, and monitoring" },
          { q: "What is an Environmental Impact Assessment?", a: "A process to predict environmental impacts of proposed projects and develop mitigation measures" },
          { q: "What factors influence geographic decision-making?", a: "Location and accessibility, environmental capacity, social impacts, economic viability, and political feasibility" }
        ],
        examTips: "Apply planning concepts to case studies. Consider multiple stakeholder perspectives."
      },
      {
        id: "unit4.3",
        title: "Appropriate Research Procedures",
        content: `
## Research Design

### Research Questions
- Clear and focused
- Researchable
- Significant
- Original contribution

### Research Types
- Descriptive
- Explanatory
- Exploratory
- Applied

### Methodology Selection
- Quantitative methods
- Qualitative methods
- Mixed methods
- Appropriate to question

## Data Collection

### Primary Data Methods

**Fieldwork:**
- Observation
- Measurement
- Sampling
- Recording

**Surveys:**
- Questionnaire design
- Interview techniques
- Focus groups
- Online surveys

### Secondary Data
- Published statistics
- Academic literature
- Government reports
- Historical records

### Sampling
- Probability sampling
- Non-probability sampling
- Sample size
- Representativeness

## Data Analysis

### Quantitative Analysis
- Descriptive statistics
- Inferential statistics
- Correlation
- Regression

### Qualitative Analysis
- Content analysis
- Thematic analysis
- Coding
- Interpretation

### Spatial Analysis
- Mapping patterns
- GIS analysis
- Spatial statistics

## Presentation

### Written Reports
- Structure and format
- Academic writing
- Referencing
- Appendices

### Visual Presentation
- Maps and diagrams
- Graphs and tables
- Photographs
- Digital presentations

### Oral Communication
- Structure
- Visual aids
- Delivery
- Questions

## Research Ethics
- Informed consent
- Confidentiality
- Data protection
- Cultural sensitivity
`,
        keyTerms: ["methodology", "sampling", "quantitative", "qualitative", "ethics", "validity"],
        practiceQuestions: [
          { q: "What makes a good research question?", a: "It should be clear, focused, researchable, significant, and make an original contribution" },
          { q: "What is the difference between probability and non-probability sampling?", a: "Probability sampling gives every element a known chance of selection; non-probability sampling does not" },
          { q: "What ethical considerations apply to geographic research?", a: "Informed consent, confidentiality, data protection, and cultural sensitivity" }
        ],
        examTips: "Demonstrate understanding of full research process. Show awareness of ethical issues."
      }
    ]
  }
};

export default {
  year11: year11GeographyStudentNotes,
  year12: year12GeographyStudentNotes,
  year13: year13GeographyStudentNotes
};
